import { Dispatch } from 'react';
import { Translate } from 'next-translate';
import { LineItemsAction } from '@providers/LineItemsProvider/action_types';
import {
  FETCH_BASELINE_PRICES,
  LINE_ITEMS_IMPORT_CONFIRM_ADD,
  LINE_ITEMS_IMPORT_ERROR,
  LINE_ITEMS_IMPORT_START,
  LINE_ITEMS_IMPORT_SUCCESS,
  LINE_ITEMS_UPLOAD_COLUMN_WARNING,
} from '@providers/LineItemsProvider/constants';
import {
  CustomHeaderKey,
  ImportResultsColumn,
  ImportResultsRecord,
  OneSchemaImportResults,
  MatchedHeader,
  StaticColumnMap
} from '@components/bid_request/details/types';
import {
  CustomColumnHeader,
  CustomColumnType,
  ID,
  Lot,
  LotErrorMap,
  User
} from 'types';
import DatadogService from '@services/DatadogService';
import { shouldAcceptSuggestedPriceImportColumn } from '@features/suggested-price-column/utils/should-accept-suggested-price-import-column';
import { buildCustomHeadersFromMatched } from './buildCustomHeadersFromMatched';
import { createNewLotsFromData } from './createNewLotsFromData';
import { createStaticColumnMap } from './createStaticColumnMap';
import { getCustomMatchedHeaders } from './getCustomMatchedHeaders';
import { updateRowDataWithStaticColumns } from './updateRowDataWithStaticColumns';

interface ProcessOneSchemaLineItemsParams {
  dispatch: Dispatch<LineItemsAction>;
  flags: {
    autofillLineItemNamesFromItemMasterAndUniqueId: boolean;
    enableAsyncHistoricalBaselinePopulation: boolean;
    isPriceUpdate: boolean;
    maxDescriptiveColumnCount: number;
    suggestedPriceEnhancementPhase: number;
    useHistoricalBaselinePopulation: boolean;
    useSuggestedPricing: boolean;
  };
  importResults: OneSchemaImportResults;
  lotErrorMap: LotErrorMap;
  t: Translate;
  uploadStartedFromEmptyLot: boolean;
  uploadStartedFromNewEvent: boolean;
  user: User;
}

/**
 * Dispatches the final state of the import process.
 * @param disableSave {Boolean}
 * @param dispatch {Dispatch<LineItemsAction>}
 * @param errorLotIndexes {Number[]}
 * @param filteredLots {Lot[]}
 * @param showColumnLimitModal {Boolean}
 * @param updatedLotErrorMap {LotErrorMap}
 * @param uploadStartedFromEmptyLot
 * @param uploadStartedFromNewEvent
 * @param userId {ID}
 * @returns {void}
 */
const dispatchImportFinalState = (
  disableSave: boolean,
  dispatch: Dispatch<LineItemsAction>,
  errorLotIndexes: number[],
  filteredLots: Lot[],
  showColumnLimitModal: boolean,
  updatedLotErrorMap: LotErrorMap,
  uploadStartedFromEmptyLot: boolean,
  uploadStartedFromNewEvent: boolean,
  userId: ID
): void => {
  const showLineItemsModal = !(uploadStartedFromEmptyLot || uploadStartedFromNewEvent);

  if (disableSave) {
    DatadogService.captureDebugMessage('Import - Completed with validation errors', {
      errorCount: errorLotIndexes.length,
      errorLotIndexes,
      successfulLots: filteredLots.length,
      userId
    });

    dispatch({
      payload: {
        disableSave,
        errorLotIndexes,
        imported: true,
        importedLots: filteredLots,
        lotErrorMap: updatedLotErrorMap,
        showLineItemsModal: true
      },
      type: LINE_ITEMS_IMPORT_ERROR
    });
  } else {
    dispatch({
      payload: {
        disableSave,
        imported: true,
        importedLots: filteredLots,
        showLineItemsModal
      },
      type: LINE_ITEMS_IMPORT_SUCCESS
    });

    if (showColumnLimitModal) {
      dispatch({
        payload: {
          showModal: true
        },
        type: LINE_ITEMS_UPLOAD_COLUMN_WARNING
      });
    }

    // Let Column Warning Modal trigger these if present.
    if (!showColumnLimitModal && (uploadStartedFromNewEvent || uploadStartedFromEmptyLot)) {
      dispatch({
        type: LINE_ITEMS_IMPORT_CONFIRM_ADD
      });
    }

    if (!showColumnLimitModal && uploadStartedFromNewEvent) {
      dispatch({
        type: FETCH_BASELINE_PRICES
      });
    }
  }
};

/**
 * Processes the line items from the OneSchema import results.
 * @param {ProcessOneSchemaLineItemsParams} params
 * @param params.dispatch {Dispatch<LineItemsAction>}
 * @param params.flags
 * @param params.importResults {OneSchemaImportResults}
 * @param params.lotErrorMap {LotErrorMap}
 * @param params.t {Translate}
 * @param params.user {User}
 * @return {void}
 */
export const processOneSchemaLineItems = ({
  dispatch,
  flags,
  user,
  importResults,
  lotErrorMap,
  t,
  uploadStartedFromEmptyLot,
  uploadStartedFromNewEvent
}: ProcessOneSchemaLineItemsParams): void => {
  const {
    isPriceUpdate,
    maxDescriptiveColumnCount,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  } = flags;
  // Same gate getTemplateOverrides uses when deciding whether to offer the column, so a mapped
  // value is honoured exactly when the column was offered.
  const acceptSuggestedPriceColumn = shouldAcceptSuggestedPriceImportColumn({
    isPriceUpdate,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  });
  const rowData: ImportResultsRecord[] = importResults.records;
  const showDescriptiveColumnsWarningModal = importResults.columns.filter(col => col.is_unmapped === true).length > maxDescriptiveColumnCount;

  let staticColumnMap: StaticColumnMap = {};
  let updatedRowData = rowData;

  DatadogService.captureDebugMessage('Import - Starting line items import', {
    columnCount: importResults.columns.length,
    recordCount: rowData.length,
    userId: user.id
  });

  dispatch({
    payload: {
      disableSave: true,
      inProgress: true,
      lotErrorMap
    },
    type: LINE_ITEMS_IMPORT_START
  });

  if (isPriceUpdate) {
    staticColumnMap = createStaticColumnMap(
      importResults.columns as ImportResultsColumn[]
    );
    updatedRowData = updateRowDataWithStaticColumns(rowData, staticColumnMap);

    DatadogService.captureDebugMessage('Import - Applied static column mapping', {
      staticColumnCount: Object.keys(staticColumnMap).length,
      updatedRowCount: updatedRowData.length,
      userId: user.id
    });
  }

  const customMatchedHeaders = getCustomMatchedHeaders(
    importResults.columns,
    staticColumnMap,
    isPriceUpdate
  );
  const descriptiveColumnType = user.global.lots.custom_column_headers.column_types.descriptive;
  const [customHeaders, customHeaderKeys] = buildCustomHeadersFromMatched(
    customMatchedHeaders as MatchedHeader[],
    descriptiveColumnType as CustomColumnType
  );
  const [filteredLots, errorLotIndexes, updatedLotErrorMap] = createNewLotsFromData({
    acceptSuggestedPriceColumn,
    customHeaderKeys: customHeaderKeys as CustomHeaderKey[],
    customHeaders: customHeaders as Partial<CustomColumnHeader>[],
    global: user.global,
    isPriceUpdate,
    lotErrorMap,
    rowData: updatedRowData,
    t,
  });
  const disableSave = errorLotIndexes.length > 0;

  DatadogService.captureDebugMessage('Import - Lot creation completed', {
    customHeadersCount: customHeaders.length,
    errorCount: errorLotIndexes.length,
    inputRowCount: updatedRowData.length,
    successfulLots: filteredLots.length,
    userId: user.id
  });

  dispatchImportFinalState(
    disableSave,
    dispatch,
    errorLotIndexes,
    filteredLots,
    showDescriptiveColumnsWarningModal,
    updatedLotErrorMap,
    uploadStartedFromEmptyLot,
    uploadStartedFromNewEvent,
    user.id
  );
};
